import { useState, useEffect } from "react";
import {
  collection, onSnapshot, query, orderBy,
  updateDoc, doc, deleteDoc
} from "firebase/firestore";
import { database } from "../Firebase";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { getFirestore, getDoc } from "firebase/firestore";

export default function MessagesAdmin() {
  const [messages,      setMessages]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState("tous");
  const [selectedMsg,   setSelectedMsg]   = useState(null);
  const [isAdmin,       setIsAdmin]       = useState(false);
  const [reponse,       setReponse]       = useState('');
  const [envoyant,      setEnvoyant]      = useState(false);
  const [reponseOk,     setReponseOk]     = useState(false);
  const navigate                          = useNavigate();

  // ── Vérification admin ────────────────────────────────────────────────────
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/login'); return; }
      try {
        const db   = getFirestore();
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!snap.exists() || snap.data().role !== 'admin') { navigate('/'); return; }
        setIsAdmin(true);
      } catch (e) { console.error("Vérification admin:", e); navigate('/'); }
    });
    return () => unsubscribe();
  }, [navigate]);

  // ── Écoute temps réel ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(
      collection(database, 'messages_contact'),
      orderBy('dateEnvoi', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(msgs);
      setLoading(false);
    });
    return () => unsub();
  }, [isAdmin]);

  // ── Ouvrir un message ─────────────────────────────────────────────────────
  const handleOpen = async (msg) => {
    setSelectedMsg(msg);
    setReponse(msg.reponseAdmin || '');
    setReponseOk(false);
    if (msg.statut === 'nouveau') await handleStatut(msg.id, 'lu');
  };

  // ── Changer statut ────────────────────────────────────────────────────────
  const handleStatut = async (id, statut) => {
    try {
      await updateDoc(doc(database, 'messages_contact', id), { statut });
      if (selectedMsg?.id === id) setSelectedMsg(prev => ({ ...prev, statut }));
    } catch (e) { console.error("updateStatut:", e); }
  };

  // ── Envoyer réponse ───────────────────────────────────────────────────────
  const handleEnvoyerReponse = async () => {
    if (!reponse.trim() || !selectedMsg) return;
    setEnvoyant(true);
    try {
      await updateDoc(doc(database, 'messages_contact', selectedMsg.id), {
        reponseAdmin:      reponse.trim(),
        dateReponseAdmin:  new Date(),
        statut:            'traite',
        reponseVue:        false, // le user n'a pas encore vu la réponse
      });
      setSelectedMsg(prev => ({
        ...prev,
        reponseAdmin:     reponse.trim(),
        dateReponseAdmin: new Date(),
        statut:           'traite',
        reponseVue:       false,
      }));
      setReponseOk(true);
      setTimeout(() => setReponseOk(false), 3000);
    } catch (e) { console.error("envoyerReponse:", e); }
    finally { setEnvoyant(false); }
  };

  // ── Supprimer réponse ─────────────────────────────────────────────────────
  const handleSupprimerReponse = async () => {
    if (!window.confirm("Supprimer la réponse ?")) return;
    try {
      await updateDoc(doc(database, 'messages_contact', selectedMsg.id), {
        reponseAdmin:     null,
        dateReponseAdmin: null,
        reponseVue:       false,
        statut:           'lu',
      });
      setSelectedMsg(prev => ({
        ...prev,
        reponseAdmin: null, dateReponseAdmin: null, statut: 'lu',
      }));
      setReponse('');
    } catch (e) { console.error("supprimerReponse:", e); }
  };

  // ── Supprimer message ─────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce message définitivement ?")) return;
    try {
      await deleteDoc(doc(database, 'messages_contact', id));
      if (selectedMsg?.id === id) setSelectedMsg(null);
    } catch (e) { console.error("deleteMsg:", e); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const statutConfig = {
    nouveau: { color: '#dc3545', bg: '#fce8e8', label: '🔴 Nouveau' },
    lu:      { color: '#f39c12', bg: '#fff8e8', label: '🟡 Lu'      },
    traite:  { color: '#07a993', bg: '#e8f8f5', label: '🟢 Traité'  },
  };

  const msgFiltres   = filter === 'tous' ? messages : messages.filter(m => m.statut === filter);
  const countByStatut = (s) => messages.filter(m => m.statut === s).length;

  if (!isAdmin) return null;

  return (
    <div style={{ padding: '16px 8px', maxWidth: '1100px', margin: '0 auto' }}>

      {/* ── En-tête ─────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 'clamp(1.2rem, 4vw, 1.6rem)', marginBottom: '4px' }}>
            ✉️ Messages de contact
          </h2>
          <p style={{ color: '#6c757d', fontSize: '0.85rem', margin: 0 }}>
            {messages.length} message{messages.length > 1 ? 's' : ''} au total
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {[
            { key: 'nouveau', label: '🔴 Nouveaux', count: countByStatut('nouveau') },
            { key: 'lu',      label: '🟡 Lus',      count: countByStatut('lu')      },
            { key: 'traite',  label: '🟢 Traités',  count: countByStatut('traite')  },
          ].map(({ key, label, count }) => (
            <div key={key} style={{
              background: statutConfig[key].bg,
              border: `1px solid ${statutConfig[key].color}`,
              borderRadius: '8px', padding: '6px 12px',
              fontSize: '0.82rem', fontWeight: 600,
              color: statutConfig[key].color,
            }}>
              {label} : {count}
            </div>
          ))}
        </div>
      </div>

      {/* ── Filtres ──────────────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {[
          { key: 'tous',    label: `Tous (${messages.length})`              },
          { key: 'nouveau', label: `Nouveaux (${countByStatut('nouveau')})` },
          { key: 'lu',      label: `Lus (${countByStatut('lu')})`           },
          { key: 'traite',  label: `Traités (${countByStatut('traite')})`   },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{
              padding: '6px 14px', borderRadius: '20px', fontSize: '0.82rem',
              fontWeight: 600, cursor: 'pointer', border: '2px solid',
              borderColor: filter === key ? '#07a993' : '#dee2e6',
              background:  filter === key ? '#07a993' : '#fff',
              color:       filter === key ? '#fff'    : '#6c757d',
              transition: 'all 0.15s',
            }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#07a993' }} />
          <p className="mt-2" style={{ color: '#6c757d', fontSize: '0.88rem' }}>
            Chargement des messages...
          </p>
        </div>
      ) : msgFiltres.length === 0 ? (
        <div className="text-center py-5" style={{
          background: '#f8f9fa', borderRadius: '12px', border: '1px dashed #dee2e6',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📭</div>
          <p style={{ color: '#6c757d', fontSize: '0.88rem', margin: 0 }}>
            Aucun message {filter !== 'tous' ? `avec le statut "${filter}"` : ''}
          </p>
        </div>
      ) : (
        <div className="row g-3">

          {/* ── Liste ───────────────────────────────────────────────────── */}
          <div className={selectedMsg ? 'col-12 col-md-5' : 'col-12'}>
            <div className="d-flex flex-column gap-2">
              {msgFiltres.map(msg => {
                const cfg        = statutConfig[msg.statut] || statutConfig.nouveau;
                const isSelected = selectedMsg?.id === msg.id;
                return (
                  <div key={msg.id} onClick={() => handleOpen(msg)}
                    style={{
                      background:   isSelected ? '#e8f8f5' : '#fff',
                      border:       `1px solid ${isSelected ? '#07a993' : '#dee2e6'}`,
                      borderLeft:   `4px solid ${cfg.color}`,
                      borderRadius: '10px', padding: '12px 14px',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8f9fa'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                            {msg.prenom} {msg.nom}
                          </span>
                          {msg.statut === 'nouveau' && (
                            <span style={{
                              fontSize: '0.6rem', background: '#dc3545',
                              color: '#fff', padding: '2px 6px',
                              borderRadius: '10px', fontWeight: 700,
                            }}>NOUVEAU</span>
                          )}
                          {/* Badge réponse envoyée */}
                          {msg.reponseAdmin && (
                            <span style={{
                              fontSize: '0.6rem', background: '#07a993',
                              color: '#fff', padding: '2px 6px',
                              borderRadius: '10px', fontWeight: 700,
                            }}>💬 Répondu</span>
                          )}
                        </div>
                        <div style={{
                          fontSize: '0.82rem', fontWeight: 600, color: '#495057',
                          marginBottom: '4px', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {msg.sujet || 'Sans sujet'}
                        </div>
                        <div style={{
                          fontSize: '0.78rem', color: '#6c757d',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {msg.message}
                        </div>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#6c757d', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {formatDate(msg.dateEnvoi)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Détail ──────────────────────────────────────────────────── */}
          {selectedMsg && (
            <div className="col-12 col-md-7">
              <div style={{
                background: '#fff', border: '1px solid #dee2e6',
                borderRadius: '12px', padding: '20px',
                position: 'sticky', top: '16px',
              }}>

                {/* En-tête */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 style={{ fontWeight: 700, marginBottom: '4px' }}>
                      {selectedMsg.prenom} {selectedMsg.nom}
                    </h5>
                    <div style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                      📅 {formatDate(selectedMsg.dateEnvoi)}
                    </div>
                  </div>
                  <button onClick={() => setSelectedMsg(null)}
                    style={{
                      background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: '1.2rem', color: '#6c757d',
                    }}>✕</button>
                </div>

                {/* Infos contact */}
                <div style={{
                  background: '#f8f9fa', borderRadius: '8px',
                  padding: '12px', marginBottom: '16px', fontSize: '0.85rem',
                }}>
                  <div className="d-flex flex-wrap gap-3">
                    <div>📧 <strong>{selectedMsg.email}</strong></div>
                    {selectedMsg.tel && <div>📞 <strong>{selectedMsg.tel}</strong></div>}
                  </div>
                </div>

                {/* Sujet */}
                <div style={{
                  background: '#e8f0fe', borderRadius: '8px',
                  padding: '8px 12px', marginBottom: '16px',
                  fontSize: '0.85rem', fontWeight: 600, color: '#1d4ed8',
                }}>
                  📌 {selectedMsg.sujet || 'Sans sujet'}
                </div>

                {/* Message user */}
                <div style={{
                  background: '#f8f9fa', borderRadius: '8px',
                  padding: '14px', marginBottom: '16px',
                  fontSize: '0.88rem', lineHeight: 1.7,
                  color: '#212529', whiteSpace: 'pre-wrap', minHeight: '80px',
                }}>
                  {selectedMsg.message}
                </div>

                {/* ── Réponse existante ─────────────────────────────────── */}
                {selectedMsg.reponseAdmin && (
                  <div style={{
                    background: '#e8f8f5', border: '1px solid #07a993',
                    borderRadius: '8px', padding: '12px', marginBottom: '16px',
                  }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ fontWeight: 700, color: '#07a993', fontSize: '0.88rem' }}>
                        💬 Votre réponse envoyée
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                        {formatDate(selectedMsg.dateReponseAdmin)}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#212529', marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                      {selectedMsg.reponseAdmin}
                    </p>
                    {/* Statut vue */}
                    <div style={{ marginTop: '8px', fontSize: '0.75rem' }}>
                      {selectedMsg.reponseVue
                        ? <span style={{ color: '#07a993' }}>✅ Vue par l'utilisateur</span>
                        : <span style={{ color: '#f39c12' }}>⏳ Pas encore vue</span>
                      }
                    </div>
                  </div>
                )}

                {/* ── Zone de réponse ───────────────────────────────────── */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '8px', display: 'block' }}>
                    {selectedMsg.reponseAdmin ? '✏️ Modifier la réponse' : '💬 Répondre à ce message'}
                  </label>
                  <textarea
                    value={reponse}
                    onChange={e => setReponse(e.target.value)}
                    placeholder="Écrivez votre réponse ici... Elle sera visible directement dans l'app de l'utilisateur."
                    rows={4}
                    maxLength={1000}
                    style={{
                      width: '100%', padding: '10px 12px',
                      borderRadius: '8px', border: '1px solid #dee2e6',
                      fontSize: '16px', resize: 'none',
                      outline: 'none', lineHeight: 1.6,
                    }}
                    onFocus={e => e.target.style.borderColor = '#07a993'}
                    onBlur={e => e.target.style.borderColor = '#dee2e6'}
                  />
                  <div className="d-flex justify-content-between mt-1">
                    <span style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                      Visible directement dans l'app de l'utilisateur
                    </span>
                    <span style={{
                      fontSize: '0.72rem',
                      color: reponse.length > 900 ? '#dc3545' : '#6c757d',
                    }}>
                      {reponse.length}/1000
                    </span>
                  </div>
                </div>

                {/* Confirmation envoi */}
                {reponseOk && (
                  <div style={{
                    background: '#d1e7dd', border: '1px solid #07a993',
                    borderRadius: '8px', padding: '8px 12px',
                    fontSize: '0.85rem', color: '#0f5132',
                    marginBottom: '12px', fontWeight: 600,
                  }}>
                    ✅ Réponse envoyée ! L'utilisateur la verra dans son espace Messages.
                  </div>
                )}

                {/* Boutons */}
                <div className="d-flex gap-2 flex-wrap mb-3">
                  {/* Envoyer réponse */}
                  <button
                    onClick={handleEnvoyerReponse}
                    disabled={!reponse.trim() || envoyant}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px',
                      background: !reponse.trim() || envoyant
                        ? '#ccc'
                        : 'linear-gradient(135deg, #07a993, #0d6efd)',
                      color: '#fff', fontWeight: 700, fontSize: '0.85rem',
                      border: 'none', cursor: !reponse.trim() ? 'not-allowed' : 'pointer',
                    }}>
                    {envoyant ? '⏳ Envoi...' : selectedMsg.reponseAdmin ? '✏️ Mettre à jour' : '📨 Envoyer la réponse'}
                  </button>

                  {/* Supprimer réponse */}
                  {selectedMsg.reponseAdmin && (
                    <button onClick={handleSupprimerReponse}
                      style={{
                        padding: '10px 14px', borderRadius: '8px',
                        border: '1px solid #f39c12', background: '#fff',
                        color: '#f39c12', fontWeight: 600, fontSize: '0.82rem',
                        cursor: 'pointer',
                      }}>
                      🗑️ Retirer
                    </button>
                  )}
                </div>

                {/* Statut + Supprimer */}
                <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '12px' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6c757d', marginBottom: '8px' }}>
                    Changer le statut :
                  </div>
                  <div className="d-flex gap-2 flex-wrap justify-content-between">
                    <div className="d-flex gap-2 flex-wrap">
                      {Object.entries(statutConfig).map(([key, cfg]) => (
                        <button key={key}
                          onClick={() => handleStatut(selectedMsg.id, key)}
                          style={{
                            padding: '6px 12px', borderRadius: '20px',
                            border: `2px solid ${cfg.color}`,
                            background: selectedMsg.statut === key ? cfg.color : '#fff',
                            color:      selectedMsg.statut === key ? '#fff'    : cfg.color,
                            fontWeight: 600, fontSize: '0.78rem',
                            cursor: 'pointer', transition: 'all 0.15s',
                          }}>
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => handleDelete(selectedMsg.id)}
                      style={{
                        padding: '6px 12px', borderRadius: '8px',
                        border: '1px solid #dc3545', background: '#fff',
                        color: '#dc3545', fontWeight: 600,
                        fontSize: '0.78rem', cursor: 'pointer',
                      }}>
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}